import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Button, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { ImageOff20Regular } from '@fluentui/react-icons';
import {
  cidReferences,
  hasRemoteImages,
  resolveCidImages,
  type Message,
} from '@gandhi/mail-protocol';
import type { MailApiClient } from '../api/client.js';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '8px' },
  text: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    font: `${tokens.fontSizeBase300}/${tokens.lineHeightBase400} ${tokens.fontFamilyBase}`,
    color: tokens.colorNeutralForeground1,
  },
  sheet: {
    padding: '4px 12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow4,
  },
  frame: { display: 'block', width: '100%', maxWidth: '100%', border: 'none' },
  notice: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
});

/** Height before the frame has been measured, and the ceiling a hostile body cannot pass. */
const UNMEASURED_HEIGHT = 160;
const MAX_HEIGHT = 200_000;

/**
 * Where a link in a message is allowed to take the reader. A scheme that is not named
 * here is not a destination: `javascript:`, `data:` and `file:` resolve to nothing, and
 * nothing is what the click does.
 */
function destinationOf(href: string): { web: URL } | { mail: URL } | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol === 'http:' || url.protocol === 'https:') return { web: url };
  if (url.protocol === 'mailto:') return { mail: url };
  return null;
}

/** A `mailto:` may be percent-encoded, and a malformed one must not break the click. */
function decoded(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** A sender who says nothing about dark mode gets the white page they wrote against. */
function sheetIsThemed(html: string): boolean {
  return /prefers-color-scheme|color-scheme/i.test(html);
}

function frameDocument(html: string, themed: boolean, remoteAllowed: boolean): string {
  const scheme = themed ? 'light dark' : 'light';
  const paper = themed ? 'Canvas' : '#ffffff';
  const ink = themed ? 'CanvasText' : '#242424';
  return [
    '<!doctype html><meta charset="utf-8">',
    `<meta http-equiv="content-security-policy" content="default-src 'none'; img-src data:${
      remoteAllowed ? ' https:' : ''
    }; style-src 'unsafe-inline'; font-src data:">`,
    '<meta name="referrer" content="no-referrer">',
    `<meta name="color-scheme" content="${scheme}">`,
    '<base target="_blank">',
    `<style>html{color-scheme:${scheme};background:${paper};color:${ink}}`,
    'body{font:14px system-ui,sans-serif;margin:0;padding:8px 0}',
    'img{max-width:100%;height:auto}</style>',
    html,
  ].join('');
}

/**
 * Received mail is somebody else's document.
 *
 * Plain text is ours to lay out, so it renders in the app's theme like any other text.
 * HTML renders in a sandboxed frame with no scripts, no forms and no navigation — a
 * boundary the browser enforces rather than one a sanitiser has to keep guessing at — on a
 * light sheet, because senders write against a white page and mostly set only their text
 * colour. A sender whose markup mentions `color-scheme` has said it can handle both, so
 * that one follows the reader's.
 *
 * The frame carries `allow-same-origin` so its height can be read and the frame sized to
 * its content, leaving the scrolling to the reading pane. That grants the frame's document
 * nothing by itself: acting on the origin needs script, and script is refused twice over,
 * by the missing `allow-scripts` and by a content-security policy of `default-src 'none'`.
 * Either alone is enough; both are measured in `e2e/message-frame.spec.ts`.
 *
 * Links are answered outside the frame, so opening one costs the sandbox no widening at
 * all: `http:` and `https:` open in a new tab, `mailto:` opens our own composer, and every
 * other scheme resolves to nothing. See `follow`.
 *
 * That policy, not the sandbox, is what keeps the sender's network out of reach — a
 * sandbox without `allow-scripts` still *fetches* the subresources it will not run. It
 * limits the frame to `data:` URLs. Parts of the message the body references by `cid:` are
 * fetched here and passed in that way, so a tracking pixel cannot report that the message
 * was opened. `allow-same-origin` gives the document a referrer to leak once the reader
 * does ask for images, which the `no-referrer` meta takes back.
 */
export function MessageBody(props: {
  message: Message;
  api: MailApiClient;
  onCompose: (to: string, subject: string) => void;
}): ReactElement {
  const styles = useStyles();
  const { message, api, onCompose } = props;
  const [inlineParts, setInlineParts] = useState<Readonly<Record<string, string>>>({});
  const [remoteAllowed, setRemoteAllowed] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const watcher = useRef<ResizeObserver | null>(null);

  // `body` shrinks back to its content; `documentElement` never drops below the frame it
  // is already in, so it could only ever grow. A body wider than the frame scrolls inside
  // it, and the bar that costs is added back so it does not force a vertical one too.
  const fit = useCallback(() => {
    const view = frame.current?.contentWindow;
    const doc = frame.current?.contentDocument;
    if (view == null || doc?.body == null) return;
    const content = doc.body.scrollHeight;
    // Nothing to go on keeps the readable fallback, rather than collapsing the message.
    if (content === 0) return;
    const scrollbar = view.innerHeight - doc.documentElement.clientHeight;
    setHeight(Math.min(content + scrollbar, MAX_HEIGHT));
  }, []);

  /**
   * The frame cannot open anything itself — it has no `allow-popups` and no
   * `allow-top-navigation` — so every click is answered here, outside it. Which means the
   * app decides what a link in a message may do, rather than the sandbox being widened
   * until the message can do it. The default is always cancelled, so a link the sandbox
   * would otherwise let load in place cannot replace the message with the linked page.
   */
  const follow = useCallback(
    (event: MouseEvent) => {
      // The frame is a realm of its own, so its elements fail `instanceof Element` here.
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (anchor == null) return;
      event.preventDefault();
      // The attribute, not the resolved `href`: a relative one would otherwise resolve
      // against this app and open it, which is never what the sender meant.
      const href = (anchor.getAttribute('href') ?? '').trim();
      const destination = destinationOf(href.startsWith('//') ? `https:${href}` : href);
      if (destination === null) return;
      if ('mail' in destination) {
        onCompose(
          decoded(destination.mail.pathname),
          destination.mail.searchParams.get('subject') ?? '',
        );
        return;
      }
      window.open(destination.web.href, '_blank', 'noopener,noreferrer');
    },
    [onCompose],
  );

  // Images land after the document does, and "Show images" reloads it entirely.
  const watch = useCallback(() => {
    watcher.current?.disconnect();
    const doc = frame.current?.contentDocument;
    if (doc?.body == null) return;
    doc.addEventListener('click', follow);
    watcher.current = new ResizeObserver(fit);
    watcher.current.observe(doc.body);
    fit();
  }, [fit, follow]);

  useEffect(() => () => watcher.current?.disconnect(), []);

  const referenced = useMemo(
    () =>
      message.attachments.filter(
        (part) => part.cid !== undefined && cidReferences(message.bodyHtml).includes(part.cid),
      ),
    [message.attachments, message.bodyHtml],
  );

  useEffect(() => {
    if (referenced.length === 0) return;
    let live = true;
    void Promise.all(
      referenced.map(async (part) => {
        const data = await api.attachmentData(message.mailboxId, message.id, part.id);
        return [part.cid as string, data] as const;
      }),
    )
      .then((entries) => {
        if (live) setInlineParts(Object.fromEntries(entries));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [api, message.id, message.mailboxId, referenced]);

  if (!message.bodyIsHtml) {
    return (
      <div className={styles.text} data-testid="message-text">
        {message.bodyText}
      </div>
    );
  }

  const themed = sheetIsThemed(message.bodyHtml);
  const html = resolveCidImages(message.bodyHtml, (cid) => inlineParts[cid]);

  return (
    <div className={styles.root}>
      {hasRemoteImages(message.bodyHtml) && !remoteAllowed && (
        <div className={styles.notice}>
          <ImageOff20Regular aria-hidden="true" />
          <Caption1>Images hosted by the sender are blocked, so opening this is private.</Caption1>
          <Button size="small" appearance="subtle" onClick={() => setRemoteAllowed(true)}>
            Show images
          </Button>
        </div>
      )}
      <div
        className={styles.sheet}
        style={{ background: themed ? tokens.colorNeutralBackground1 : '#ffffff' }}
      >
        <iframe
          ref={frame}
          className={styles.frame}
          style={{ height: `${height ?? UNMEASURED_HEIGHT}px` }}
          title={`Message body from ${message.from.address}`}
          data-testid="message-body"
          sandbox="allow-same-origin"
          referrerPolicy="no-referrer"
          onLoad={watch}
          srcDoc={frameDocument(html, themed, remoteAllowed)}
        />
      </div>
    </div>
  );
}
